import { styled } from '@mui/material/styles';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { OutlinedInputProps } from '@mui/material/OutlinedInput';
import { forwardRef, KeyboardEvent, FormEvent, useRef, ReactNode } from 'react';

import { InputAdornment } from '@mui/material';
import { formatToId } from '../../utils/helper';
import { NUMBER, TEXT } from '../constants/components';

const DEFAULT_PROPS: TextFieldProps = {
  variant: 'filled',
  size: 'small',
};

const CustomMuiTextField = styled(({ slotProps, ...props }: TextFieldProps) => (
  <TextField
    slotProps={{
      input: { disableUnderline: true, ...slotProps?.input } as Partial<OutlinedInputProps>,
    }}
    {...props}
    {...DEFAULT_PROPS}
  />
))(({ theme }) => ({
  label: {
    position: 'relative',
    backgroundColor: 'transparent !important',
    color: `${theme.palette.neutral[600]} !important`,
    textAlign: 'start',
    width: '100%',
    transformOrigin: 'unset',
    transform: 'unset',
    fontSize: '14.71px',
    paddingBottom: '2px',
  },
  '& .MuiFilledInput-root': {
    backgroundColor: 'white',
    border: `1px solid ${theme.palette.neutral[200]} !important`,
    overflow: 'hidden',
    borderRadius: 8,
    input: {
      paddingTop: 12,
      paddingBottom: 12,
    },
    transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    '&:hover:not(.Mui-error):not(.Mui-focused)': {
      boxShadow: `${theme.palette.green[500]} 0 0 0 1px`,
      borderColor: theme.palette.green[500],
    },
    '&.Mui-focused': {
      boxShadow: `${theme.palette.green[500]} 0 0 0 4px`,
      borderColor: theme.palette.green[500],
    },
    '&.Mui-error': {
      boxShadow: `${theme.palette.red[200]} 0 0 0 2px`,
    },
    '&.Mui-error:hover': {
      boxShadow: `${theme.palette.red[500]} 0 0 0 1px`,
    },
    '&.Mui-focused.Mui-error': {
      backgroundColor: 'white',
      boxShadow: `${theme.palette.red[500]} 0 0 0 4px`,
    },
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    fontSize: `14px !important`,
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: theme.palette.red[500],
  },
  '& .MuiFormHelperText-root.Mui-disabled': {
    backgroundColor: 'transparent',
  },
  '& .MuiFormHelperText-root.Mui-disabled:hover': {
    backgroundColor: 'transparent',
  },
  '& .MuiFormLabel-root:not(.Mui-error)': {
    color: theme.palette.neutral[600],
  },
  '& .Mui-disabled': {
    backgroundColor: theme.palette.neutral[100],
    boxShadow: 'none !important',
  },
}));

type IMuiTextField = {
  label: string;
  allowNegative?: boolean;
  maxLength?: number;
  errorText?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
} & TextFieldProps;

const MuiTextField = forwardRef((props: IMuiTextField, ref) => {
  const {
    label,
    id,
    name,
    inputProps,
    allowNegative,
    maxLength,
    errorText,
    error,
    helperText,
    InputLabelProps,
    InputProps,
    startAdornment,
    endAdornment,
    ...cleanProps
  } = props;
  const _id = formatToId(id || String(label));

  const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.currentTarget.type === 'number' && !allowNegative)
      (NUMBER.KEYS.PREVENTS as ReadonlyArray<string>).includes(e.key) && e.preventDefault();
  };

  const handleOnInput = (e: FormEvent<HTMLInputElement>) => {
    const len = e.currentTarget.value.length;
    const maxLen = maxLength || NUMBER.LIMIT.SHORT;
    if (e.currentTarget.type === 'number' && len > maxLen) {
      e.currentTarget.value = e.currentTarget.value.slice(0, maxLen);
    }
  };

  const hasError = error ?? Boolean(errorText);

  const _ref = useRef<HTMLInputElement>();

  return (
    <CustomMuiTextField
      id={_id}
      name={name || _id}
      label={label}
      inputRef={ref ?? _ref}
      inputProps={{
        max: props.type === 'date' ? '9999-12-31' : undefined,
        maxLength: maxLength || TEXT.MAX.NAME,
        ...inputProps,
        onKeyDown: handleOnKeyDown,
        onInput: handleOnInput,
      }}
      onClick={() => {
        if (cleanProps.type === 'date' || cleanProps.type === 'datetime-local' || cleanProps.type === 'time') {
          _ref?.current?.showPicker();
        }
      }}
      error={hasError}
      helperText={helperText ?? errorText}
      slotProps={{
        input: {
          startAdornment: startAdornment ? <InputAdornment position="start">{startAdornment}</InputAdornment> : undefined,
          endAdornment: endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : undefined,
          ...InputProps,
        },
      }}
      {...cleanProps}
      InputLabelProps={{
        shrink: true,
        ...InputLabelProps,
      }}
    />
  );
});
MuiTextField.displayName = 'MuiTextField';

export default MuiTextField;
